import { GRID_SIZE } from "@/constants/gridCellLayouts";
import { useCurrMapInteractionState } from "@/models/map-interaction/mapInteractionStateHooks";
import { isDisabledGrid } from "@/models/map-interaction/mapInteractionStateUtils";
import { useAppDispatch } from "@/reducer";
import { useCountryPlots } from "@/reducer/CountryPlotState";
import { useCrossSections } from "@/reducer/CrossSectionState";
import { useEnergyPlots } from "@/reducer/EnergyPlotsState";
import { switchLayouts, useLayouts } from "@/reducer/LayoutsState";
import { useLocationTables } from "@/reducer/LocationTablesState";
import { useMaps } from "@/reducer/MapsState";
import { useNotes } from "@/reducer/NoteState";
import { usePlots } from "@/reducer/PlotsState";
import { useTab } from "@/reducer/TabsState";
import { useTephigrams } from "@/reducer/TephigramState";
import { useWeatherTables } from "@/reducer/WeatherTablesState";
import { Trans } from "@lingui/react/macro";
import { useCallback, useEffect, useMemo } from "react";
import { GridLayoutCell } from "./GridLayoutCell";

interface GridProps {
  tabId: number;
}

const gridDefinition = {
  display: "grid",
  gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
  gridTemplateRows: `repeat(${GRID_SIZE}, 1fr)`,
};

export function Grid({ tabId }: GridProps) {
  const { data: tab, isLoading, hasError } = useTab();
  const dispatch = useAppDispatch();
  const plots = usePlots(tabId);
  const weatherTables = useWeatherTables(tabId);
  const locationTables = useLocationTables(tabId);
  const maps = useMaps(tabId);
  const energyPlots = useEnergyPlots(tabId);
  const layouts = useLayouts(tabId);
  const tephigrams = useTephigrams(tabId);
  const crossSections = useCrossSections(tabId);
  const countryPlots = useCountryPlots(tabId);
  const notes = useNotes(tabId);

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [layouts]);

  const changeLayoutPosition = useCallback(
    (sourceId: number, targetId: number) => {
      if (layouts && sourceId !== targetId) {
        dispatch(switchLayouts({ layoutId1: sourceId, layoutId2: targetId, tabId }));
      }
    },
    [layouts, tabId, dispatch],
  );

  const tools = useMemo(
    () => ({ maps, plots, energyPlots, weatherTables, locationTables, tephigrams, crossSections, countryPlots, notes }),
    [maps, plots, energyPlots, weatherTables, locationTables, tephigrams, crossSections, countryPlots, notes],
  );

  const mapInteractionState = useCurrMapInteractionState();

  if (hasError) {
    return <Trans id="tab.fetch.failure">Failed to load tab from server</Trans>;
  }
  if (isLoading || !tab) {
    return <mm-loader size="xlarge" />;
  }

  return (
    <div className="grid workspace-grid grid--no-gap cover-window" style={gridDefinition}>
      {layouts.map((layout) => (
        <GridLayoutCell
          key={layout.id}
          layout={layout}
          tabId={tabId}
          tools={tools}
          disabled={isDisabledGrid(mapInteractionState, layout)}
          onDrop={changeLayoutPosition}
        />
      ))}
    </div>
  );
}
